import { extractAuthThrottleMeta, resolveApiErrorDetail } from '@/lib/auth-errors';
import { formatApiErrorDetail } from '@/lib/format-api-error-detail';

export const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://127.0.0.1:8000';

export function resolveWebAdminApiBaseUrl(): string {
  return API_URL.replace(/\/$/, '');
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: unknown,
    public data?: any
  ) {
    const detail =
      data && typeof data === 'object' && data.detail !== undefined ? data.detail : message;
    const fallback =
      typeof message === 'string' && message.trim()
        ? message.trim()
        : typeof data?.error === 'string' && data.error.trim()
          ? data.error.trim()
          : typeof data?.message === 'string' && data.message.trim()
            ? data.message.trim()
            : 'Ошибка запроса';
    super(formatApiErrorDetail(detail, fallback));
    this.name = 'ApiError';
  }
}

export function buildQueryString(params: Record<string, any> = {}): string {
  const sp = new URLSearchParams();
  for (const [key, raw] of Object.entries(params)) {
    if (raw === undefined || raw === null || raw === '') continue;
    if (typeof raw === 'boolean') {
      sp.append(key, raw ? 'true' : 'false');
      continue;
    }
    sp.append(key, String(raw));
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export class ApiClientCore {
  protected baseURL: string;
  private refreshPromise: Promise<boolean | null> | null = null;
  private logoutInFlight = false;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  /**
   * Web-admin перешла на cookie-only сессию (HttpOnly `__Host-ppc_access` / `__Host-ppc_refresh`).
   * `getToken()` оставлен для обратной совместимости со старыми бандлами/legacy-кодом и
   * всегда возвращает `null` — мы больше не храним access_token в `localStorage`.
   *
   * После раскатки в проде нужно один раз почистить старые ключи, чтобы локальный заглавный
   * Bearer не пересиливал свежую куку. Делаем это лениво при первом запросе.
   */
  private getToken(): string | null {
    return null;
  }

  protected clearLegacyLocalStorageTokensOnce(): void {
    if (typeof window === 'undefined') return;
    try {
      if (localStorage.getItem('access_token') || localStorage.getItem('refresh_token')) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      }
    } catch {
      /* private-mode safari etc. */
    }
  }

  /** Single-flight refresh: один POST /auth/refresh на все параллельные 401. */
  protected async refreshSession(): Promise<boolean | null> {
    if (typeof window === 'undefined') return null;
    if (this.refreshPromise) return this.refreshPromise;

    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${this.baseURL}/api/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Client': 'web' },
          credentials: 'include',
          body: '{}',
        });
        if (response.status === 401) return null;
        if (!response.ok) return false;
        try {
          const data = (await response.json()) as { user?: any };
          if (data?.user) {
            localStorage.setItem('user', JSON.stringify(data.user));
          }
        } catch {
          /* server may return empty body */
        }
        return true;
      } catch {
        return false;
      } finally {
        this.refreshPromise = null;
      }
    })();
    return this.refreshPromise;
  }

  protected handleAuthFailure(reason: 'unauthorized' | 'manual'): void {
    if (typeof window === 'undefined') return;
    if (this.logoutInFlight) return;
    this.logoutInFlight = true;
    try {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('web_admin_bootstrap');
    } catch {
      /* noop */
    }
    if (reason === 'unauthorized' && !window.location.pathname.startsWith('/login')) {
      window.location.href = '/login';
    }
  }

  /** Унифицированный fetch с кукой и retry-on-401 после refresh. Не выбрасывает logout на сетевых ошибках. */
  async authedFetch(input: string, init: RequestInit = {}): Promise<Response> {
    this.clearLegacyLocalStorageTokensOnce();
    const headers = new Headers(init.headers || {});
    if (!headers.has('X-Client')) headers.set('X-Client', 'web');

    const performOnce = async (): Promise<Response> => {
      return fetch(input, { ...init, headers, credentials: 'include' });
    };

    let response: Response;
    try {
      response = await performOnce();
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      throw new ApiError(0, reason || 'Сеть недоступна', { network: true });
    }

    if (response.status !== 401) return response;
    const refreshed = await this.refreshSession();
    if (refreshed === null) {
      this.handleAuthFailure('unauthorized');
      return response;
    }
    if (refreshed === false) {
      // Сетевая/5xx-ошибка на /auth/refresh — оставляем сессию, отдаём первый 401 наверх.
      return response;
    }
    try {
      return await performOnce();
    } catch (err) {
      const reason = err instanceof Error ? err.message : String(err);
      throw new ApiError(0, reason || 'Сеть недоступна', { network: true });
    }
  }

  async request<T = any>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    const response = await this.authedFetch(url, { ...options, headers });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const normalizedDetail = resolveApiErrorDetail(errorData);
      const throttleMeta = extractAuthThrottleMeta(response, errorData);
      throw new ApiError(
        response.status,
        formatApiErrorDetail(
          normalizedDetail,
          errorData.error ?? errorData.message ?? response.statusText
        ),
        { ...errorData, detail: normalizedDetail, ...throttleMeta }
      );
    }

    if (response.status === 204) return undefined as T;
    return response.json();
  }

  async get<T = any>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /** GET без парсинга JSON (CSV, бинарники). */
  async getBlob(endpoint: string): Promise<Blob> {
    const url = `${this.baseURL}${endpoint}`;
    const response = await this.authedFetch(url, { method: 'GET' });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        errorData.detail || errorData.error || response.statusText,
        errorData
      );
    }
    return response.blob();
  }

  async post<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T = any>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T = any>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  getUser(): any {
    if (typeof window === 'undefined') return null;
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  setUser(user: any) {
    if (typeof window === 'undefined') return;
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }
}
